import React, { useEffect, useState } from "react";
import { Typography, Tag, Flex, Tooltip } from "antd";
import { UserOutlined } from "@ant-design/icons";
import get from "lodash/get";
import map from "lodash/map";
import round from "lodash/round";
import { checkNullable, getTransformedMinutes, normalize, safeAdd } from "../../../utilities/helpers";
import DashboardTemplateButton, {
  DashboardTemplateDropdown,
} from "../../buttons/dashboardTemplateButton";
import { cardTitleBg, cardTitleWidth, getStatusTagColor, renderProgress } from "../../../constants/andOnDashBoard";

const { Text, Paragraph } = Typography;

const PunchPressTemplateCard = ({ themeClasses, template, stats, isFullScreen }) => {
  const [part, setPart] = useState(get(stats, "parts.[0]"));
  const statusValue = get(stats, "status.value");
  const statusName = get(stats, "status.name");

  useEffect(() => {
    setPart(get(stats, "parts[0]"));
  }, [template, stats]);
  return (
    <div
      className={`
        ${get(themeClasses, "cardShadow")} border ${get(
        themeClasses,
        "cardBorder"
      )}
        flex flex-col justify-between rounded-xl p-0 w-full
      `}
    >
      {/* Header */}
      <div
        className={`py-1 px-2 border-b rounded-t-xl text-center ${get(
          themeClasses,
          "mainBorder"
        )} ${cardTitleBg[statusValue] ? cardTitleBg[statusValue] : "bg-gray-900"}`}
      >
        <div className="flex items-center justify-between flex-wrap space-x-2">
          <Text
            className={`text-base text-left font-medium ${get(
              themeClasses,
              "titleColor"
            )}`}
            style={{ width: cardTitleWidth[statusValue] ? cardTitleWidth[statusValue] : 120 }}
            ellipsis={{ tooltip: `${get(template, "name")}` }}
          >
            {get(template, "name")}
          </Text>
          <Flex>
            <Flex wrap gap="small">
              <DashboardTemplateButton title="shift" template={template} />
              <DashboardTemplateButton title="breakdown" template={template} />
              <DashboardTemplateButton
                title="time_graph_reports"
                template={template}
              />
            </Flex>
            <Text
              className={`text-xs font-medium ${get(
                themeClasses,
                "textSecondary"
              )} ml-3`}
            >
              Status:
              <Tag
                color={getStatusTagColor(statusName)}
                style={{ padding: "0.2rem 0.2rem" }}
                className="text-xs font-semibold ml-2 my-1/2 w-auto px-4 inline-block min-w-[40px] text-center h-auto py-2"
              >
                {statusName}
              </Tag>
            </Text>
          </Flex>
        </div>
      </div>

      {/* Body */}
      <div className="flex flex-col flex-grow">
        {/* Left Info */}
        <div className={`flex flex-row flex-1 border-b ${get(
          themeClasses,
          "mainBorder"
        )}`}>
          <div className="text-white flex-1 text-center flex justity-center items-center">
            <div className="text-xs w-full items-center justify-center">
              <Tooltip title={get(stats, "operator", "")} className="mx-auto">
                <Paragraph ellipsis={{ rows: 1, expandable: false }} style={{ width: 100 }} className="text-white">
                  <UserOutlined className="mr-1" />
                  {get(stats, "operator", "--")}
                </Paragraph>
              </Tooltip>
            </div>
          </div>
          <div className={`flex-1 border-l ${get(
            themeClasses,
            "mainBorder"
          )}`}>

            <DashboardTemplateDropdown
              themeClasses={themeClasses}
              stats={stats}
              setPart={setPart}
              part={part}

            />
          </div>
        </div>
        <div className={`flex flex-row flex-grow border-b ${get(
          themeClasses,
          "mainBorder"
        )}`}>
          <div
            className={`py-3 px-2 text-left flex justify-between flex-1`}
          >
            <div className="flex-1">
              <h4
                className={`mb-0 text-xs font-normal ${get(
                  themeClasses,
                  "titleColor"
                )}`}
              >
                COUNT
              </h4>
              <p
                className={`${isFullScreen ? "text-md" : "text-lg"
                  } font-semibold text-white`}
              >
                {`${get(part, "count.actual", get(stats, "count.actual", 0))
                  ? round(get(part, "count.actual", get(stats, "count.actual", 0)) || 0)
                  : 0
                  } / ${get(part, "count.target_count", get(stats, "count.target_count", 0))
                    ? round(get(
                      part,
                      "count.target_count",
                      get(stats, "count.target_count", 0)
                    ) || 0)
                    : 0
                  }`}
              </p>
            </div>
          </div>
          {map(
            [
                {
                label: "ON / DOWN",
                value: `${getTransformedMinutes(
                  normalize(get(stats, "on_time", 0))
                )} / ${getTransformedMinutes(safeAdd(get(stats, "idle_time", 0),get(stats, "off_time", 0)))}`,
              },
              {
                label: "UNIT",
                value: `${get(stats, "total_power", 0)
                  ? round(get(stats, "total_power", 0), 1)
                  : 0
                  } Kwh`,
              },
            ],
            ({ label, value }) => (
              <div
                key={label}
                className={`py-1.5 px-2 border-l ${get(
                  themeClasses,
                  "mainBorder"
                )} text-left flex-1`}
              >
                <h4
                  className={`mb-0 text-xs font-normal ${get(
                    themeClasses,
                    "titleColor"
                  )}`}
                >
                  {label}
                </h4>
                <p
                  className={`${isFullScreen ? "text-xs" : "text-[16px]"
                    } font-semibold text-white text-center`}
                >
                  {value ? value : 0}
                </p>
              </div>
            )
          )}
        </div>
        <div
          className={`${get(
            themeClasses,
            "mainBorder"
          )} text-left flex-grow overflow-hidden border-b`}
        >
          <div className="flex justify-between h-full">
            <div
              className={`px-1 py-1 border-r ${get(
                themeClasses,
                "mainBorder"
              )} text-left flex flex-col justify-between items-center flex-1`}
            >
              <p className="text-xs font-semibold text-white text-center">
                Curr Avg/hr
              </p>
              <p
                className={`text-center font-semibold ${get(part, "count.current_hourly_avg", 0) <
                  get(part, "count.planned_hourly_avg", 0)
                  ? "text-red-600"
                  : "text-green-600"
                  }`}
              >
                {get(part, "count.current_hourly_avg")
                  ? round(get(part, "count.current_hourly_avg"))
                  : 0}
              </p>
            </div>
            <div
              className={`px-1 py-1 border-r ${get(
                themeClasses,
                "mainBorder"
              )} text-left flex flex-col justify-between items-center flex-1`}
            >
              <p className="text-xs font-semibold text-white text-center">
                Plan Avg/hr
              </p>
              <p className="text-center font-semibold text-white">
                {get(part, "count.planned_hourly_avg")
                  ? round(get(part, "count.planned_hourly_avg"))
                  : 0}
              </p>
            </div>
            <div
              className={`px-1 py-1 text-left flex flex-col justify-between items-center flex-1 text-blink`}
              style={{
                background: get(part, "count.current_hourly_avg", 0) <
                  get(part, "count.planned_hourly_avg", 0)
                  ? "#A02020"
                  : "#008141"
              }}
            >

              <p className="text-xs font-semibold text-white text-center">
                Est. Count
              </p>
              <p
                className={`text-center font-semibold 
                 text-white
                  `}
              >
                {get(part, "count.estimated_count")
                  ? round(get(part, "count.estimated_count") || 0)
                  : 0}{" "}
                <span
                  className="text-xs"
                >
                  / {checkNullable(round(get(part, "count.count_difference"), 0) || 0)}
                </span>
              </p>
            </div>
          </div>
        </div>

        {/* Right Progress Bars */}
        <div
          className={`flex-shrink-0 flex flex-roe justify-between items-center`}
        >

          <div
            className={`flex flex-1 flex-col items-center justify-center border-r ${get(
              themeClasses,
              "mainBorder"
            )} w-full py-1.5`}
          >
            <h4
              className={`mb-1 text-sm font-semibold ${get(
                themeClasses,
                "titleColor"
              )}`}
            >
              Production
            </h4>
            {renderProgress(
              round(get(
                part,
                "count.production_avg",
                get(stats, "count.production_avg", 0)
              ) || 0, 1),
              themeClasses
            )}
          </div>
          <div className={`flex flex-1 flex-col items-center w-full py-1.5`}>
            <h4
              className={`mb-1 text-sm font-semibold ${get(
                themeClasses,
                "titleColor"
              )}`}
            >
              OEE
            </h4>
            {renderProgress(round(get(stats, "meta.oee", 0) || 0, 1), themeClasses)}
          </div>
        </div>
      </div>
    </div>
  );
}

export default PunchPressTemplateCard